import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { withRouter } from 'react-router-dom';
import Raven from 'raven-js';

import fullname from 'utils/fullname';

import Users from 'api/resources/users';
import getErrors from 'utils/getErrors';
import { Deserializer } from 'jsonapi-serializer';

import UserPanel from 'admin-moderator/components/UserPanel';

import RightColumnBody from 'styled/Page/RightColumnBody';
import Loader from 'components/Loader';

export class UserPage extends React.PureComponent {
  state = {
    user: undefined,
    errors: [],
    loading: true,
  }

  componentDidMount() {
    this.reloadUser();
  }

  componentDidUpdate(prevProps) {
    if (this.props.match.params.userId !== prevProps.match.params.userId) {
      this.reloadUser();
    }
  }

  componentWillUnmount() {
    if (this.abortController) this.abortController.abort();
  }

  reloadUser = async () => {
    if (this.abortController) this.abortController.abort();
    if (typeof window.AbortController === 'function') {
      this.abortController = new window.AbortController();
    }

    this.setState({
      user: undefined,
      errors: [],
      loading: true,
    });

    try {
      const response = await Users.get({
        userId: this.props.match.params.userId,
        include: 'moderator_room,tech_room,avatar,current_ban,oauth_providers',
        fields: {
          user: 'first_name,last_name,avatar,phone,current_ban,tech_room,moderator_room,material_counters,oauth_providers',
        },
        signal: this.abortController ? this.abortController.signal : undefined,
      });

      const user = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);
      this.setState({ user, loading: false });
    } catch (err) {
      const errors = await getErrors(err);
      this.setState({ errors, loading: false });

      Raven.captureException(err, { extra: this.props });
    }
  }

  handleBan = (user) => {
    this.setState(prevState => ({
      user: { ...prevState.user, currentBan: user.currentBan },
    }));

    this.props.onBan(user);
  }

  handleChatRoomStarted = (newChatRoom) => {
    if (newChatRoom.roomType !== 'moderator_chat') return;

    this.setState(prevState => ({
      user: { ...prevState.user, moderatorRoom: newChatRoom },
    }));
  }

  handleChatRoomFinished = (newChatRoom) => {
    if (newChatRoom.roomType !== 'moderator_chat') return;

    this.setState(prevState => ({
      user: { ...prevState.user, moderatorRoom: newChatRoom },
    }));
  }

  handleAdminNotificationsMessage = (response) => {
    if (!response || !response.action) return;

    switch (response.action) {
      case 'lock':
      case 'unlock':
        this.handleLockAndUnlock(response);
        break;
      case 'move_cursor':
        this.handleMoveCursor(response);
        break;
      default:
        Raven.captureException('Unsupported WebSocket action', { extra: response });
        break;
    }
  }

  handleLockAndUnlock = (response) => {
    if (!response || !response.body || response.type !== 'chat_room') return;
    if (!this.state.user) return;

    if (this.state.user.moderatorRoom && this.state.user.moderatorRoom.id !== response.body.id) return;

    this.setState(prevState => ({
      user: {
        ...prevState.user,
        moderatorRoom: {
          ...prevState.user.moderatorRoom,
          locked: response.body.locked,
          lockedEmployeeFullname: response.body.locked_employee_fullname,
          lockedEmployeeId: response.body.locked_employee_id,
        },
      },
    }));
  }

  handleMoveCursor = (response) => {
    if (!response || !response.body || response.type !== 'chat_room') return;
    if (!this.state.user) return;

    if (this.state.user.moderatorRoom && this.state.user.moderatorRoom.id === response.body.id) {
      this.setState(prevState => ({
        user: {
          ...prevState.user,
          moderatorRoom: {
            ...prevState.user.moderatorRoom,
            hasNewMessages: response.body.has_new_messages,
          },
        },
      }));
    }

    if (this.state.user.techRoom && this.state.user.techRoom.id === response.body.id) {
      this.setState(prevState => ({
        user: {
          ...prevState.user,
          techRoom: {
            ...prevState.user.techRoom,
            hasNewMessages: response.body.has_new_messages,
          },
        },
      }));
    }
  }

  render() {
    return (
      <React.Fragment>
        <Helmet><title>{fullname(this.state.user) || this.props.match.params.userId}</title></Helmet>

        {this.state.user && (
          <UserPanel
            id={this.state.user.id}
            avatarUrl={this.state.user.avatar && this.state.user.avatar.url}
            phone={this.state.user.phone}
            isBanned={Boolean(this.state.user.currentBan)}
            techRoom={this.state.user.techRoom}
            moderatorRoom={this.state.user.moderatorRoom}
            fullName={fullname(this.state.user)}
            imagesCount={this.state.user.materialCounters && this.state.user.materialCounters.images}
            videosCount={this.state.user.materialCounters && this.state.user.materialCounters.videos}
            acceptedImageCount={this.state.user.materialCounters && this.state.user.materialCounters.acceptedImages}
            acceptedVideoCount={this.state.user.materialCounters && this.state.user.materialCounters.acceptedVideos}
            socialLinks={this.state.user.oauthProviders}

            onBan={this.handleBan}
            onChatRoomStarted={this.handleChatRoomStarted}
            onChatRoomFinished={this.handleChatRoomFinished}
            onAdminNotificationsMessage={this.handleAdminNotificationsMessage}
          />
        )}

        {this.state.errors.length > 0 && (
          <RightColumnBody>
            {this.state.errors.map(message => (
              <p key={message} role="alert">{message}</p>
            ))}
          </RightColumnBody>
        )}

        {!this.state.loading && !this.state.errors.length && !this.state.user && (
          <RightColumnBody>
            <p role="alert">Пользователя не удалось загрузить</p>
          </RightColumnBody>
        )}

        {this.state.loading && (<RightColumnBody><Loader /></RightColumnBody>)}
      </React.Fragment>
    );
  }
}

UserPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({ userId: PropTypes.string }),
  }).isRequired,

  onBan: PropTypes.func,
};

UserPage.defaultProps = {
  onBan: () => {},
};

export default withRouter(UserPage);
